import { capitalize } from "lodash";
import moment from "moment";

export class AnalyticsGeneralInfo {
  constructor(
    monitorName = "",
    selectedPlates = [],
    totalDefects = 0,
    totalDefectsPerType = [],
    startDate = "",
    endDate = "",
    error = ""
  ) {
    /** @type {string} */
    this.monitorName = monitorName;
    /** @type {string[]}*/
    this.selectedPlates = selectedPlates;
    /** @type {number}*/
    this.totalDefects = totalDefects;
    /** @type {Object}*/
    this.totalDefectsPerType = totalDefectsPerType;
    /** @type {string[]}*/
    this.defectsLabels = totalDefectsPerType
      .reduce((acc, cur) => {
        acc.push(cur.type);
        return acc;
      }, [])
      .sort(BatchPredictionMonitorAnalytics.sortAlphabetically);
    /** @type {string[]}*/
    this.defectsSeries = totalDefectsPerType.reduce((acc, cur) => {
      const defectIndex = this.defectsLabels.indexOf(cur.type);
      if (defectIndex > -1) {
        acc[defectIndex] = cur.value;
      }
      return acc;
    }, []);
    /** @type {string}*/
    this.startDate = startDate ? moment(startDate, "YYYYMMDDHHmmss").format("YYYY-MM-DD HH:mm:ss") : "";
    /** @type {string}*/
    this.endDate = endDate ? moment(endDate, "YYYYMMDDHHmmss").format("YYYY-MM-DD HH:mm:ss") : "";
    /** @type {boolean}*/
    this.loading = false;
    /** @type {string} */
    this.error = error;
  }

  static fromApi({
    monitor_name,
    selected_plates,
    total_defects,
    total_defects_per_type,
    start_date,
    end_date,
    error,
  }) {
    return new AnalyticsGeneralInfo(
      monitor_name,
      selected_plates,
      total_defects,
      total_defects_per_type,
      start_date,
      end_date,
      error
    );
  }
}

export class AnalyticsJudgementInfo {
  constructor(judgementInfo = {}) {
    /** @type {Object}*/
    this.judgementInfo = judgementInfo;
    /** @type {string[]}*/
    this.dataCategories = Object.keys(judgementInfo) || [];
    /** @type {Object[]}*/
    this.dataSeries = BatchPredictionMonitorAnalytics.getDataSeries(judgementInfo, this.dataCategories) || [];
    /** @type {boolean}*/
    this.loading = false;
  }
}

export class AnalyticsDefectsInfo {
  constructor(defectsInfo = {}) {
    /** @type {Object}*/
    this.defectInfo = defectsInfo;
    /** @type {string[]}*/
    this.defectCategories = Object.keys(defectsInfo);
    /** @type {Object[]}*/
    this.defectSeries = BatchPredictionMonitorAnalytics.getDataSeries(defectsInfo, this.defectCategories).sort((a, b) =>
      BatchPredictionMonitorAnalytics.sortAlphabetically(a.name, b.name)
    );
    /** @type {boolean}*/
    this.loading = false;
  }
}

export class PlatesImagesInfo {
  constructor(plateImagesAnalytics = {}, loading = false) {
    /** @type {Object}*/
    this.platesImagesInfo = plateImagesAnalytics;
    /** @type {string[]}*/
    this.platesImagesCategories = Object.keys(plateImagesAnalytics);
    /** @type {Object[]}*/
    this.platesImagesSeries = BatchPredictionMonitorAnalytics.getDataSeries(
      plateImagesAnalytics,
      this.platesImagesCategories
    ).sort((a, b) => BatchPredictionMonitorAnalytics.sortAlphabetically(a.name, b.name));
    /** @type {boolean}*/
    this.loading = loading;
  }
  setLoading(loading) {
    this.loading = loading;
    return this;
  }
}

const SIDES = {
  A: "A",
  B: "B",
  C: "C",
  D: "D",
};
export class BatchPredictionMonitorAnalytics {
  SIDE_PREFIX = "Side_";

  static getDataSeries(data, categories) {
    const series = [];
    const labelToIndex = {};
    categories.forEach((category, index) => {
      Object.keys(data[category]).forEach((label) => {
        // replace with Object.hasOwn when it's supported
        if (!labelToIndex.hasOwnProperty(label)) {
          labelToIndex[label] = series.length;
          series.push({
            name: capitalize(label),
            data: new Array(categories.length).fill(0),
          });
        }
        series[labelToIndex[label]].data[index] = data[category][label];
      });
    });
    return series;
  }

  static MAX_LEN = 152;

  static SIDE_PREFIX = "Side_";

  static sideToPoint = {
    [SIDES.A]: (x, y) => [this.MAX_LEN - x, this.MAX_LEN - y],
    [SIDES.B]: (x, y) => [y, this.MAX_LEN - x],
    [SIDES.C]: (x, y) => [x, y],
    [SIDES.D]: (x, y) => [this.MAX_LEN - y, x],
  };

  calculatePointForSide(point) {
    const { x, y } = point;

    return [x, y];
  }

  getScatterSeries(data) {
    const series = [];
    const judgementToIndex = {};
    Object.keys(data).forEach((judgement) => {
      // replace with Object.hasOwn when it's supported
      if (!judgementToIndex.hasOwnProperty(judgement)) {
        judgementToIndex[judgement] = series.length;
        series.push({
          name: capitalize(judgement),
          data: [],
        });
      }
      data[judgement].forEach((point) => {
        series[judgementToIndex[judgement]].data.push(this.calculatePointForSide(point));
      });
    });
    return series;
  }

  /*
   * @param {string} a
   * @param {string} b
   * @returns {number}
   * @memberof BatchPredictionMonitorAnalytics
   * @static
   * @description Sorts alphabetically by first letter
   * this is used to sort the defects labels so every defect is in the same order and has the same color in all charts
   */
  static sortAlphabetically(a, b) {
    if (a < b) {
      return -1;
    }
    if (a > b) {
      return 1;
    }
    return 0;
  }
}

export class JudgementPointsInfo extends BatchPredictionMonitorAnalytics {
  constructor(judgementPoints = {}) {
    super();
    this.judgementPoints = judgementPoints;
    this.judgementPointsSeries = this.getScatterSeries(judgementPoints).sort((a, b) =>
      BatchPredictionMonitorAnalytics.sortAlphabetically(a.name, b.name)
    );
  }
}
